import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet';
import { Box, Button, Typography, Dialog, DialogActions, DialogTitle, DialogContent, CircularProgress } from '@mui/material';
import * as XLSX from 'xlsx';

import VisitsListTable from './VisitsListTable';
import VisitsFilter from './VisitsFilter';
import VisitsCreateDialog from './VisitsCreateDialog';
import AppPagination from '../../../components/AppPagination';

import { resetRequirementsState } from '../../../slices/requirementsSlice';

import { createNewRequirement, buildRequirementPayload } from './Config/FirebaseFunctions';
import MessageDialog from '../../../utils/MessageDialog';

import { fetchAndFillExcel, fecthhAndFillExcelImages, fetchAndFillExcelQuestions } from '../Visits/Config/VisitsMapping';
import { fixTextThunk } from '../../../actions/chatGptActions';
import { getCompanyObject } from '../../../actions/companiesActions';

import { getDatabase, ref, onValue, query, orderByChild, equalTo, get, update } from "firebase/database";
import { firebaseConfig } from './Config/firebaseConfig';
import { initializeApp } from "firebase/app";

// Firebase initialization (outside the component)
const app = initializeApp(firebaseConfig);
const db = getDatabase(app);

// Default filter values for visits
const defaultFilter = {
  name: '',
  identification: ''
};

// Component styles
const styles = {
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
};

// Main Visits component
const Visits = () => {
  const dispatch = useDispatch();
  // Get company information from Redux store
  const companyId = useSelector(state => state.auth.profile.companyRef.name);
  const companyIdToGet = useSelector(state => state.auth.profile.companyRef._id);

  // State variables
  const [visits, setVisits] = useState([]); // Stores list of visits
  const fileInputRef = useRef(null); // Reference for file input element

  const [filter, setFilter] = useState({ ...defaultFilter }); // Filter state
  const [pagination, setPagination] = useState({ page: 1 }); // Pagination state
  const [openNew, setOpenNew] = useState(false); // Controls new visit dialog

  // Dialog state variables
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');

  const [openDialog, setOpenDialog] = useState(false); // Controls visit details dialog
  const [visitData, setVisitData] = useState(null); // Stores selected visit data
  const [loading, setLoading] = useState(false); // Loading state

  // Function to show message dialog
  const showDialog = (title, message) => {
    setDialogTitle(title);
    setDialogMessage(message);
    setDialogOpen(true);
  };

  // Maps Excel row data to form data structure
  const mapRowToFormData = row => ({
    nombre: row.nombre || '',
    documento: row.documento || '',
    direccion: row.direccion || '',
    telefono: row.telefono || ''
  });

  // Validates form data from Excel import
  const isValidFormData = data => {
    const requiredFields = [
      'nombre',
      'documento',
      'direccion',
      'telefono'
    ];

    // Check if all required fields are present and not empty
    const hasAllFields = requiredFields.every(field => {
      const value = data[field];
      return value !== undefined && value !== null && value.toString().trim() !== '';
    });

    if (!hasAllFields) return false;

    // Basic phone number validation (only digits, minimum 10 characters)
    const phoneRegex = /^\d{10,}$/;
    if (!phoneRegex.test(data.telefono)) return false;
    return true;
  };

  // FIREBASE MODULES
  // Firebase text fix function and send it to ChatGpt
  const testFix = async (visit, setLoading, dispatch, step) => {
    setLoading(true);

    // Check if text has already been fixed
    const fixedTextRef = ref(db, `FixedText/${visit.requirementId}/fixedText`);
    const snapshot = await get(fixedTextRef);
    const urlImage = await getUrlCompany(visit.companyName.trim());

    if (snapshot.exists() && snapshot.val() === true) {

      await fetchAndFillExcel(visit, setLoading, dispatch, visitData.concept.conceptText, step, urlImage).finally(() => visit.listImages != null ? setLoading(true) : setLoading(false));

      if (visit.listImages != null) {
        await fecthhAndFillExcelImages(visit, setLoading, dispatch, step, urlImage).finally(() => visit.specificQuestions != null ? setLoading(true) : setLoading(false));
      }

      if (visit.specificQuestions != null) {
        await fetchAndFillExcelQuestions(visit, setLoading, dispatch, step, urlImage).finally(() => setLoading(false));
      }

    } else {
      try {
        // If not fixed, call ChatGPT API to fix text
        const response = await dispatch(fixTextThunk({ originalText: visit.concept.conceptText })).unwrap();
        updateConcept(visit, setLoading, dispatch, response.text, step, urlImage);
      } catch (error) {
        showDialog('Error', 'Error al corregir el texto: ' + error);
        setLoading(false);
      }
    }
  };

  // Get URL of the company and return it
  // This function is used to get the URLImage of the company for the visit
  const getUrlCompany = useCallback(async (id) => {
    try {
      const response = await dispatch(getCompanyObject({ id })).unwrap();
      return response.company.urlPicture;
    } catch (error) {
      // Return default image if company image is not available
      return "https://firebasestorage.googleapis.com/v0/b/cristopher-data-451401.firebasestorage.app/o/companies%2FGC360.png?alt=media&token=6d3963a5-9c36-4d0b-92c3-cde19e144fab";
    }
  }, [dispatch, showDialog]);

  // Helper function to translate visit status
  const visitStatus = (status) => status === 'Finished' ? 'Finalizada' : 'Pendiente';

  // Update concept function into firebase and then fetch the excel
  const updateConcept = useCallback(async (visit, setLoading, dispatch, text, step, url) => {
    const updates = {};
    updates[`FixedText/${visit.requirementId}/fixedText`] = true;
    updates[`visits/${visit.requirementId}/concept/conceptText`] = text;

    try {
      const db = getDatabase(); // Get database instance
      const dbRef = ref(db); // Create root reference
      await update(dbRef, updates); // Apply updates

      await fetchAndFillExcel(visit, setLoading, dispatch, text, step, url).finally(() => visit.listImages != null ? setLoading(true) : setLoading(false));

      if (visit.listImages != null) {
        await fecthhAndFillExcelImages(visit, setLoading, dispatch, step, url).finally(() => visit.specificQuestions != null ? setLoading(true) : setLoading(false));
      }

      if (visit.specificQuestions != null) {
        await fetchAndFillExcelQuestions(visit, setLoading, dispatch, step, url).finally(() => setLoading(false));
      }

    } catch (error) {
      showDialog('Error', 'Error al actualizar el concepto: ' + error);
      setLoading(false);
    }
  }, [showDialog]);

  // Fetch visits by company name from Firebase
  const fetchVisitsByCompany = useCallback((companyName) => {
    const visitsRef = query(
      ref(db, 'visitControl'),
      orderByChild('companyName'),
      equalTo(companyName)
    );

    // Listen for real-time updates
    onValue(
      visitsRef,
      (snapshot) => {
        const data = snapshot.val();
        if (data) {
          // Convert object to array and set visits state
          setVisits(Object.keys(data).map(key => ({ id: key, ...data[key] })));
        } else {
          setVisits([]); // No visits for this company
        }
      },
      (error) => {
        showDialog('Error al cargar visitas', 'Hubo un problema al cargar las visitas. Por favor, inténtelo de nuevo.' + error.message);
      }
    );
  }, [showDialog]);

  // Get visit by ObjectId
  // This function is used to get the visit by ObjectId and then open the dialog with the visit data
  const getVisitByObjectId = useCallback(async (objectId) => {
    try {
      const visitRef = ref(db, `visits/${objectId}`);
      const snapshot = await get(visitRef);
      if (snapshot.exists()) {
        setVisitData(snapshot.val());
        setOpenDialog(true);
      } else {
        showDialog('Advertencia', 'Visita no encontrada o no iniciada!');
      }
    } catch (error) {
      showDialog("Error", `Error obteniendo la visita: ${error.message}`);
    }
  }, [showDialog]);

  // Handle bulk upload from Excel file
  const handleFileUpload = event => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = e => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet); // Convert Excel to JSON

        const errorRows = [];

        // Process each row
        json.forEach(async (row, index) => {
          const formData = mapRowToFormData(row);
          if (isValidFormData(formData)) {
            try {
              const payload = buildRequirementPayload(formData, companyId);
              await createNewRequirement(payload);
            } catch (error) {
              errorRows.push(`Fila ${index + 2}: ${JSON.stringify(row)}\n`);
            }
          } else {
            errorRows.push(`Fila ${index + 2}: ${JSON.stringify(row)}`);
          }
        });

        // Show alert with errors if any
        if (errorRows.length > 0) {
          showDialog('Advertencia', `❌ Las siguientes filas presentaron errores y no se cargaron:\n\n${errorRows.join('\n')}\n\nPor favor, NO vuelva a cargar todo, sólo las filas con error!.`);
        } else {
          showDialog('Éxito', '✅ Carga masiva completada sin errores');
        }
      };
      reader.readAsArrayBuffer(file);
    }
  };

  // Trigger file input click
  const handleButtonClick = () => fileInputRef.current.click();

  // Handle dialog close
  const handleCloseDialog = () => setOpenDialog(false);

  // Cleanup on component unmount
  useEffect(() => {
    setPagination({ page: 1 });
    return () => dispatch(resetRequirementsState());
  }, []);

  // Fetch requirements when companyId changes
  useEffect(() => {
    if (companyId) {
      fetchVisitsByCompany(companyId);
    }
  }, [companyId, fetchVisitsByCompany]);

  // Event handlers
  const handleChange = e => setFilter({ ...filter, [e.target.name]: e.target.value });
  const onSubmit = e => {
    e.preventDefault();
    setPagination({ page: 1 });
  };
  const onClear = () => setFilter({ ...defaultFilter });
  const nextPage = () => setPagination({ page: pagination.page + 1 });
  const prevPage = () => setPagination({ page: Math.max(pagination.page - 1, 1) });

  const handleOpenNew = () => setOpenNew(true);
  const handleCloseNew = () => setOpenNew(false);

  // Function to download template file from firestorage
  const downloadTemplate = () => {
    const fileUrl = "https://firebasestorage.googleapis.com/v0/b/cristopher-data-451401.firebasestorage.app/o/files%2FCarga%20Masiva%20Visitas.xlsx?alt=media&token=d2e95911-65dd-4052-8b60-22aaf2a5fed5";

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "Carga Masiva Visitas.xlsx"; // File name
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Helmet>
        <title>Cristopher | Visitas</title>
      </Helmet>

      <Box sx={styles.title}>
        <Typography variant="h5" fontWeight="bold">Visitas Domiciliarias</Typography>
        <Box display="flex" gap={1}>
          <Button onClick={handleOpenNew}>Solicitar Prueba</Button>
          <input
            type="file"
            accept=".xlsx, .xls"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
          <Button onClick={handleButtonClick} color="primary">Solicitar Masivo</Button>
          <Button onClick={downloadTemplate} color="primary">Descargar plantilla</Button>
        </Box>
      </Box>

      <VisitsFilter {...filter} onChange={handleChange} onSubmit={onSubmit} onClear={onClear} />
      <VisitsListTable requirements={visits} onDownloadClick={getVisitByObjectId} />
      <AppPagination page={pagination.page} prev={prevPage} next={nextPage} />
      <VisitsCreateDialog open={openNew} onClose={handleCloseNew} companyRef={companyId} />

      <MessageDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={dialogTitle}
        message={dialogMessage}
      />

      {/* Visit details dialog */}
      <VisitDialog
        open={openDialog}
        onClose={handleCloseDialog}
        visitData={visitData}
        loading={loading}
        onDownload={() => {
          setLoading(true);
          testFix(visitData, setLoading, dispatch, "");
        }}
        visitStatus={visitStatus}
      />
    </>
  );
};

// Dialog component for displaying visit details
// These components are used to show the visit details and error messages respectively
const VisitDialog = ({ open, onClose, visitData, loading, onDownload, visitStatus }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" >
    <DialogTitle>Detalles de la Visita</DialogTitle>
    <DialogContent dividers>
      {visitData && (
        <>
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <img
              src={visitData.basicData.urlPicture}
              alt="Visitado"
              style={{
                width: 150,
                height: 150,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #eee'
              }}
            />
          </div>
          {[
            `ID: ${visitData.requirementId}`,
            `Documento: ${visitData.basicData.documentNumber}`,
            `Nombre: ${visitData.basicData.name}`,
            `Dirección: ${visitData.basicData.address}`,
            `Compañía: ${visitData.companyName}`,
            `Teléfono: ${visitData.basicData.phoneNumber}`,
            `Estado: ${visitStatus(visitData.step)}`
          ].map((text, i) => (
            <Typography key={i} paragraph sx={{ mb: 1 }}>{text}</Typography>
          ))}
        </>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cerrar</Button>
      <Button
        onClick={onDownload}
        disabled={!visitData || loading || visitData.step !== "Finished"}
        startIcon={loading && <CircularProgress size={20} />}
      >
        Descargar Informe
      </Button>
    </DialogActions>
  </Dialog>
);

export default Visits;